import { useCallback, useEffect, useState } from 'react';
import { procurementApi } from '@/api/procurement.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function ContractsPage() {
  const pagination = usePagination();
  const [items, setItems] = useState<Awaited<ReturnType<typeof procurementApi.listContracts>>['content']>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    procurementApi
      .listContracts({ page: pagination.page, size: pagination.size })
      .then((p) => {
        setItems(p.content);
        pagination.applyPageResponse(p);
      })
      .catch((e) => showApiError(e, 'Failed to load contracts'))
      .finally(() => setLoading(false));
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const runReorder = async () => {
    try {
      const created = await procurementApi.runReorder();
      notify.success(`Auto-reorder created ${created.length} purchase request(s)`);
    } catch (err) {
      showApiError(err, 'Auto-reorder failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Contracts & reorder"
        description="Supplier contracts and inventory-driven auto-reorder"
        breadcrumbs={[{ label: 'Procurement', to: '/procurement' }, { label: 'Contracts' }]}
        actions={<Button onClick={() => void runReorder()}>Run auto-reorder</Button>}
      />
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left dark:border-slate-700">
              <th className="p-3">Number</th>
              <th className="p-3">Title</th>
              <th className="p-3">Status</th>
              <th className="p-3">End date</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} className="p-4 text-slate-500">No contracts yet — create via API or shared contracts module.</td></tr>
            ) : (
              items.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="p-3">{c.contractNumber}</td>
                  <td className="p-3">{c.title}</td>
                  <td className="p-3">{c.contractStatus}</td>
                  <td className="p-3">{c.endDate ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <TablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
          loading={loading}
          hasRows={items.length > 0}
        />
      </div>
    </div>
  );
}
